import React from 'react';
import { Alert, Box, Button, Chip, Link, Typography } from '@mui/material';
import type { DuplicateMatch } from '../../types/duplicateMatch';

/**
 * Non-blocking "did you mean?" panel for the Farmer / Farm / Plot create dialogs (Plan C, Layer 1).
 * Purely presentational: it lists the existing entities that look like the row about to be created and
 * offers, per row, "Use this instead" plus a single "Add anyway" dismissal. It NEVER gates submit —
 * the create button stays enabled regardless of what is shown here.
 */
interface DuplicateMatchPanelProps {
  matches: DuplicateMatch[];
  /** Heading noun, e.g. "farmer" / "farm" / "plot". */
  entityNoun: string;
  /** Singular noun for `childCount`, e.g. "farm" for a farmer, "plot" for a farm. Omit for plots. */
  childNoun?: string;
  /** "Use this instead" — the dialog closes without creating (and calls its own onUseExisting). */
  onUseExisting: (match: DuplicateMatch) => void;
  /** "Add anyway" — hide the panel for the current input. */
  onDismiss: () => void;
}

/** Chip copy: an exact hit reads "matched on email"; a soft hit reads "possible match". */
const matchChipLabel = (m: DuplicateMatch): string =>
  m.confidence === 'Low' ? 'possible match' : `matched on ${m.reason.toLowerCase()}`;

/** Secondary line: parent context + child/sample tallies, whichever are present. */
const contextCaption = (m: DuplicateMatch, childNoun?: string): string => {
  const parts: string[] = [];
  if (m.parentContext) parts.push(m.parentContext);
  if (childNoun && m.childCount > 0) {
    parts.push(`${m.childCount} ${childNoun}${m.childCount === 1 ? '' : 's'}`);
  }
  if (m.sampleCount > 0) parts.push(`${m.sampleCount} sample${m.sampleCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
};

export const DuplicateMatchPanel: React.FC<DuplicateMatchPanelProps> = ({
  matches,
  entityNoun,
  childNoun,
  onUseExisting,
  onDismiss,
}) => {
  if (matches.length === 0) return null;

  return (
    <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        You may already have this {entityNoun}:
      </Typography>
      {matches.map((m) => {
        const caption = contextCaption(m, childNoun);
        return (
          <Box
            key={`${m.type}-${m.id}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 0.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {m.label}
                </Typography>
                <Chip
                  label={matchChipLabel(m)}
                  size="small"
                  variant="outlined"
                  color={m.confidence === 'Low' ? 'default' : 'warning'}
                />
              </Box>
              {caption && (
                <Typography variant="caption" color="text.secondary">
                  {caption}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onUseExisting(m)}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Use this instead
            </Button>
          </Box>
        );
      })}
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={onDismiss}
        sx={{ mt: 1, fontSize: '0.8rem' }}
      >
        Add anyway
      </Link>
    </Alert>
  );
};

export default DuplicateMatchPanel;
